/**
 * The choices behind the filter controls (design §5.2): categories, labels, lists and the
 * team, each loaded once for the sources the register uses, and the names of the organisations and
 * persons behind the ids in the filter (`GET /refs`). `known` says which ids exist, per source, once
 * that source has loaded: `sanitizeFilter` drops what is not there, but keeps a uuid it cannot check
 * yet, and one whose source failed to load.
 *
 * The components in this folder take their data and searches from the `FilterOptions` they are
 * given and call no API themselves, so they render (and are tested) without one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toast } from '@/lib/toast'
import { listContacts } from '../api'
import { listChoices } from '../lists/api'
import { listOrganisations } from '../organisations/api'
import { getRefs, listImportOptions } from '../query/api'
import type { Filter, OptionDef, OptionsDef, PropertyDef, Register } from '../query/types'
import { isUuid, type KnownIds } from '../query/validate'
import { listCategories, listManagedLabels } from '../settings/api'
import type { CategoryScope } from '../settings/types'

export type RefKind = 'organisations' | 'persons'

/** One hit of the search behind an organisation or person picker. */
export interface RefHit {
  id: string
  name: string
  /** The city of an organisation, the organisation of a person. */
  detail: string | null
}

export interface FilterOptions {
  /** The choices of a property: its own for static ones, the loaded ones otherwise; null while they load, or for a property without choices. */
  optionsFor(property: PropertyDef): OptionDef[] | null
  /** Every loaded id and every resolved organisation or person id, mapped to its name. */
  names: ReadonlyMap<string, string>
  known: KnownIds
  /** Searches the organisations or persons a picker offers (at least two characters). Without it a picker finds nothing. */
  searchRefs?: (kind: RefKind, term: string) => Promise<RefHit[]>
  /** The name of an organisation or person that was just picked, so its chip does not read "Onbekend" until `GET /refs` answers. */
  remember?: (kind: RefKind, id: string, name: string) => void
}

const LOAD_FAILED = 'Kon de keuzelijsten niet laden.'
const CATEGORY_SCOPES: readonly string[] = ['relatie', 'afspraak', 'lijst']

interface Row {
  id: string
  name: string
}

function categoryScope(scope: string | null): CategoryScope {
  return CATEGORY_SCOPES.includes(scope ?? '') ? (scope as CategoryScope) : 'relatie'
}

/** What is loaded once: labels, lists (also dynamic and archived), and the categories of each scope. */
function sourceKey(options: OptionsDef | null): string | null {
  if (!options) return null
  if (options.source === 'categories') return `categories:${categoryScope(options.scope)}`
  return options.source === 'labels' || options.source === 'lists' || options.source === 'import_batches'
    ? options.source
    : null
}

function loadSource(key: string): Promise<Row[]> {
  if (key === 'labels') return listManagedLabels()
  if (key === 'import_batches') return listImportOptions()
  // spec-63 E5: "Lijsten bevat" works on static and dynamic lists; archived ones are marked.
  if (key === 'lists') return listChoices().then((lists) => lists.map((list) => ({ id: list.id, name: list.archived ? `${list.name} (gearchiveerd)` : list.name })))
  return listCategories(key.slice('categories:'.length) as CategoryScope)
}

/** The loaded rows per source key; null is a source that failed. */
interface Sources {
  register: Register | null
  rows: Readonly<Record<string, Row[] | null>>
}

/** The organisations and persons that were answered, by id; an id that is not in a map does not exist. */
interface RefState {
  organisations: ReadonlyMap<string, string>
  persons: ReadonlyMap<string, string>
  /** `kind:id` of every id that was answered, found or not. */
  answered: ReadonlySet<string>
}

interface Wanted {
  organisations: string[]
  persons: string[]
}

const NO_ROWS: Sources['rows'] = {}
const NO_REFS: RefState = { organisations: new Map(), persons: new Map(), answered: new Set() }

/** The organisation and person ids a filter names, wherever the register says an id of that kind goes. */
function wantedRefs(register: Register | null, filter: Filter | null): Wanted {
  const wanted = { organisations: new Set<string>(), persons: new Set<string>() }
  if (register && filter) {
    for (const condition of [...filter.quick, ...filter.groups.flat()]) {
      const source = register.properties.find((property) => property.key === condition.property)?.options?.source
      if ((source !== 'organisations' && source !== 'persons') || !Array.isArray(condition.value)) continue
      for (const id of condition.value) if (typeof id === 'string' && isUuid(id)) wanted[source].add(id)
    }
  }
  return { organisations: [...wanted.organisations].sort(), persons: [...wanted.persons].sort() }
}

const toOptions = (rows: readonly Row[]): OptionDef[] => rows.map((row) => ({ value: row.id, label: row.name }))

async function searchRefs(kind: RefKind, term: string): Promise<RefHit[]> {
  if (kind === 'organisations') {
    const result = await listOrganisations({ q: term, page_size: 10 })
    return result.items.map((item) => ({ id: item.id, name: item.name, detail: item.city }))
  }
  const result = await listContacts({ q: term, limit: 10 })
  return result.items.map((item) => ({
    id: item.person_id,
    name: item.full_name,
    detail: item.primary_organisation?.name ?? null,
  }))
}

/**
 * `register` and `filter` are null until the page has them. `filter` is the one on the page: its
 * organisation and person ids are looked up once each, and a new one (picked, or from a link) when
 * it shows up.
 */
export function useFilterOptions(register: Register | null, filter: Filter | null): FilterOptions {
  const [sources, setSources] = useState<Sources>({ register: null, rows: NO_ROWS })
  const [refState, setRefState] = useState<RefState>(NO_REFS)
  const requested = useRef(new Set<string>())
  const toasted = useRef(false)
  const { membersById } = useTeamMembers()

  // One message for any number of failed loads.
  const failed = useCallback(() => {
    if (toasted.current) return
    toasted.current = true
    toast.error(LOAD_FAILED)
  }, [])

  useEffect(() => {
    if (!register) return
    const keys = new Set<string>()
    for (const property of register.properties) {
      const key = sourceKey(property.options)
      if (key) keys.add(key)
    }
    const store = (key: string, rows: Row[] | null) =>
      setSources((previous) => ({
        register,
        rows: { ...(previous.register === register ? previous.rows : NO_ROWS), [key]: rows },
      }))
    for (const key of keys) {
      loadSource(key).then(
        (rows) => store(key, rows),
        () => {
          failed()
          store(key, null)
        },
      )
    }
  }, [register, failed])

  // The ids to look up, as a string: `filter` may be a new object on every render, its ids rarely are.
  const wantedKey = JSON.stringify(wantedRefs(register, filter))
  useEffect(() => {
    const wanted = JSON.parse(wantedKey) as Wanted
    const todo: Wanted = {
      organisations: wanted.organisations.filter((id) => !requested.current.has(`organisations:${id}`)),
      persons: wanted.persons.filter((id) => !requested.current.has(`persons:${id}`)),
    }
    if (todo.organisations.length + todo.persons.length === 0) return
    for (const id of todo.organisations) requested.current.add(`organisations:${id}`)
    for (const id of todo.persons) requested.current.add(`persons:${id}`)
    getRefs({ organisationIds: todo.organisations, personIds: todo.persons }).then(
      (refs) =>
        setRefState((previous) => {
          const organisations = new Map(previous.organisations)
          for (const organisation of refs.organisations) organisations.set(organisation.id, organisation.name)
          const persons = new Map(previous.persons)
          for (const person of refs.persons) persons.set(person.person_id, person.full_name)
          const answered = new Set(previous.answered)
          for (const id of todo.organisations) answered.add(`organisations:${id}`)
          for (const id of todo.persons) answered.add(`persons:${id}`)
          return { organisations, persons, answered }
        }),
      failed, // the ids stay unanswered: they are kept as they are, and not asked again
    )
  }, [wantedKey, failed])

  const remember = useCallback((kind: RefKind, id: string, name: string) => {
    requested.current.add(`${kind}:${id}`)
    setRefState((previous) => ({
      organisations: kind === 'organisations' ? new Map(previous.organisations).set(id, name) : previous.organisations,
      persons: kind === 'persons' ? new Map(previous.persons).set(id, name) : previous.persons,
      answered: new Set(previous.answered).add(`${kind}:${id}`),
    }))
  }, [])

  // The team by content: a new Map with the same members must not make new options, names and ids.
  const teamJson = membersById
    ? JSON.stringify(
        [...membersById.values()].map((member) => [member.id, member.display_name]).sort((a, b) => a[1].localeCompare(b[1], 'nl')),
      )
    : null
  const team = useMemo<Row[] | null>(
    () => (teamJson === null ? null : (JSON.parse(teamJson) as [string, string][]).map(([id, name]) => ({ id, name }))),
    [teamJson],
  )

  return useMemo<FilterOptions>(() => {
    // Nothing is known without a register: no property to look the choices of up.
    if (!register) return { optionsFor: () => null, names: new Map(), known: {}, searchRefs, remember }
    const names = new Map<string, string>()
    const known: KnownIds = {}
    const lists = new Map<string, OptionDef[]>()
    const loaded = sources.register === register ? sources.rows : NO_ROWS
    for (const [key, rows] of Object.entries(loaded)) {
      lists.set(key, toOptions(rows ?? []))
      if (!rows) continue
      const source = key.split(':')[0] as 'categories' | 'labels' | 'lists' | 'import_batches'
      // Only the latest imports are offered: an older one in a link is still a valid id.
      if (source === 'import_batches') {
        for (const row of rows) names.set(row.id, row.name)
        continue
      }
      known[source] = new Set([...(known[source] ?? []), ...rows.map((row) => row.id)])
      for (const row of rows) names.set(row.id, row.name)
    }
    // A team that came back empty is a failed load (the shared fetch swallows errors): no ids to check against.
    if (team && team.length > 0) {
      known.users = new Set(team.map((member) => member.id))
      for (const member of team) names.set(member.id, member.name)
    }
    const users = team ? [{ value: 'me', label: 'Ik' }, { value: 'none', label: 'Niemand' }, ...toOptions(team)] : null

    const wanted = JSON.parse(wantedKey) as Wanted
    for (const kind of ['organisations', 'persons'] as const) {
      const found = refState[kind]
      for (const [id, name] of found) names.set(id, name)
      // Known once every id in the filter has been answered; until then (or after a failure) it cannot be checked.
      if (wanted[kind].every((id) => refState.answered.has(`${kind}:${id}`))) known[kind] = new Set(found.keys())
    }

    return {
      optionsFor(property) {
        const spec = property.options
        if (!spec) return null
        if (spec.source === 'static') return spec.values
        if (spec.source === 'users') return users
        const key = sourceKey(spec)
        return key ? (lists.get(key) ?? null) : null
      },
      names,
      known,
      searchRefs,
      remember,
    }
  }, [register, sources, refState, team, wantedKey, remember])
}
